import { Typography } from '@euler/app/components/typography';
import { aiColors, tint } from '@euler/app/flows/ai-suite/theme';
import { EmptyView } from '@euler/components/EmptyView';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsDesktopLayout } from '@euler/functions/responsiveness';
import {
  CoachingCaseBrowseRequest,
  CoachingCaseCard,
  CoachingLibraryCatalogue,
} from '@euler/model/coaching';
import { SlidersHorizontal } from 'lucide-react-native';
import { memo, useMemo, useRef } from 'react';
import {
  Pressable,
  ScrollView,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import { CaseCard } from '../../components/CaseCard';
import { CaseCardSkeleton } from '../../components/CaseCardSkeleton';
import { CaseTags } from '../../components/CaseTags';
import { CategoryChips } from '../../components/CategoryChips';
import { CollectionStrip } from '../../components/CollectionStrip';
import { SearchBox } from '../../components/SearchBox';
import { SelectedFilters } from '../../components/SelectedFilters';
import {
  CaseFilterKey,
  CaseFilterValue,
  SelectedCaseFilter,
  caseFilterCount,
} from '../../functions/caseFilters';
import { CaseSearchState } from '../../functions/caseSearchStates';

type Collection = CoachingLibraryCatalogue['collections'][number];

function skeletons(count: number, style: StyleProp<ViewStyle>) {
  return Array.from({ length: count }, (_, index) => (
    <View key={index} style={style}>
      <CaseCardSkeleton />
    </View>
  ));
}

/** How close to the list's end the next page starts loading. */
const kLoadAheadPx = 600;
/** Fewer cases than this fit one look: no "all shown" line. */
const kEndNoticeMin = 10;

/** The case library: search, categories, collections and the cases. */
export const CatalogView = memo(
  ({
    request,
    search,
    catalogue,
    selectedFilters,
    onKeyword,
    onCategory,
    onCollection,
    onOpenCollection,
    onAllCollections,
    onOpenFilters,
    onClearFilters,
    onRemoveFilter,
    onOpen,
    onRetry,
    onLoadMore,
  }: {
    request: CoachingCaseBrowseRequest;
    search: CaseSearchState;
    catalogue: CoachingLibraryCatalogue | null;
    /** The applied filters, labelled. */
    selectedFilters: SelectedCaseFilter[];
    onKeyword: (keyword: string) => void;
    onCategory: (slug: string | null) => void;
    /** Leaves the collection the route opened the library on. */
    onCollection: (collection: Collection | null) => void;
    onOpenCollection: (collection: Collection) => void;
    onAllCollections: () => void;
    onOpenFilters: () => void;
    onClearFilters: () => void;
    onRemoveFilter: (key: CaseFilterKey, value: CaseFilterValue) => void;
    onOpen: (card: CoachingCaseCard) => void;
    onRetry: () => void;
    /** Asks for the next page; ignored while one loads or when all are in. */
    onLoadMore: () => void;
  }) => {
    const styles = useStyles();
    const { rpx } = useResponsiveContext();
    const wide = useIsDesktopLayout();
    const filters = caseFilterCount(request);
    const collection = catalogue?.collections.find(
      item => item.slug === request.collection,
    );
    const browsing =
      !request.keyword && !request.collection && !request.category && !filters;
    const total = search.kind === 'ready' ? search.total : null;
    // The next page loads as the list's end comes near — and at once while
    // the list is shorter than the screen, which no scroll would reach.
    const viewport = useRef({ offset: 0, height: 0, content: 0 });
    const nearEnd = () => {
      const { offset, height, content } = viewport.current;
      if (height && offset + height >= content - rpx(kLoadAheadPx))
        onLoadMore();
    };
    return (
      <ScrollView
        contentContainerStyle={styles.page}
        scrollEventThrottle={100}
        onScroll={({ nativeEvent: x }) => {
          viewport.current.offset = x.contentOffset.y;
          viewport.current.height = x.layoutMeasurement.height;
          viewport.current.content = x.contentSize.height;
          nearEnd();
        }}
        onLayout={({ nativeEvent: x }) => {
          viewport.current.height = x.layout.height;
          nearEnd();
        }}
        onContentSizeChange={(_, height) => {
          viewport.current.content = height;
          nearEnd();
        }}
      >
        <View style={styles.padded}>
          <SearchBox value={request.keyword ?? ''} onChange={onKeyword} />
        </View>
        <View style={styles.chips}>
          <CategoryChips
            categories={catalogue?.categories ?? []}
            selected={request.category ?? null}
            total={browsing ? total : null}
            onSelect={onCategory}
          />
        </View>
        {/* Collections are their own way in: a search or filter leaves
            them be, only the collection the route opened on replaces them. */}
        {!request.collection && catalogue?.collections.length ? (
          <View style={[styles.padded, styles.section]}>
            <View style={styles.row}>
              <Typography.Text size={rpx(16)} bold color={aiColors.foreground}>
                系列合集
              </Typography.Text>
              <Pressable
                onPress={onAllCollections}
                accessibilityRole="link"
                hitSlop={rpx(8)}
              >
                <Typography.Text size={rpx(12)} color={aiColors.primary}>
                  全部合集 →
                </Typography.Text>
              </Pressable>
            </View>
            <View style={styles.strip}>
              <CollectionStrip
                collections={catalogue.collections}
                onOpen={onOpenCollection}
              />
            </View>
          </View>
        ) : null}
        {collection ? (
          <View style={[styles.padded, styles.section, styles.row]}>
            <Typography.Text size={rpx(15)} bold color={aiColors.foreground}>
              {`合集 · ${collection.name}`}
            </Typography.Text>
            <Pressable onPress={() => onCollection(null)}>
              <Typography.Text size={rpx(13)} color={aiColors.primary}>
                看全部
              </Typography.Text>
            </Pressable>
          </View>
        ) : null}
        <View style={[styles.padded, styles.section, styles.row]}>
          <Typography.Text size={rpx(13)} color={aiColors.mutedForeground}>
            {total == null ? '' : `共 ${total} 个场景`}
          </Typography.Text>
          <View style={styles.actions}>
            {filters ? (
              <Pressable onPress={onClearFilters}>
                <Typography.Text size={rpx(13)} color={aiColors.primary}>
                  清空筛选
                </Typography.Text>
              </Pressable>
            ) : null}
            <Pressable
              onPress={onOpenFilters}
              accessibilityRole="button"
              style={[styles.filterButton, filters ? styles.filterOn : null]}
            >
              <SlidersHorizontal
                size={rpx(16)}
                color={filters ? aiColors.primary : aiColors.foreground}
              />
              <Typography.Text
                size={rpx(13)}
                medium
                color={filters ? aiColors.primary : aiColors.foreground}
              >
                {filters ? `筛选 ${filters}` : '筛选'}
              </Typography.Text>
            </Pressable>
          </View>
        </View>
        {selectedFilters.length ? (
          <View style={[styles.padded, styles.selected]}>
            <SelectedFilters
              filters={selectedFilters}
              onRemove={onRemoveFilter}
            />
          </View>
        ) : null}
        <View style={styles.padded}>
          {search.kind === 'loading' ? (
            <View style={[styles.grid, wide && styles.gridWide]}>
              {skeletons(wide ? 4 : 3, wide ? styles.cellWide : null)}
            </View>
          ) : search.kind === 'failed' ? (
            <EmptyView
              message="案例暂时没加载出来"
              onRetry={onRetry}
              noFullHeight
            />
          ) : search.cases.length === 0 ? (
            <EmptyView
              message="没找到？清空筛选，或者回到上一页直接说说你遇到的情况"
              noFullHeight
            />
          ) : (
            <>
              <View style={[styles.grid, wide && styles.gridWide]}>
                {search.cases.map(card => (
                  <View key={card.id} style={wide ? styles.cellWide : null}>
                    <CaseCard
                      card={card}
                      onPress={() => onOpen(card)}
                      footer={<CaseTags tags={card.tags} max={3} />}
                    />
                  </View>
                ))}
              </View>
              {search.more === 'loading' ? (
                <View style={[styles.grid, wide && styles.gridWide]}>
                  {skeletons(wide ? 2 : 1, wide ? styles.cellWide : null)}
                </View>
              ) : search.more === 'failed' ? (
                <Pressable
                  onPress={onLoadMore}
                  accessibilityRole="button"
                  style={styles.more}
                >
                  <Typography.Text
                    size={rpx(13)}
                    color={aiColors.primary}
                    style={styles.centered}
                  >
                    没加载出来，点击重试
                  </Typography.Text>
                </Pressable>
              ) : search.cases.length >= search.total &&
                search.total >= kEndNoticeMin ? (
                <Typography.Text
                  size={rpx(12)}
                  color={aiColors.mutedForeground}
                  style={[styles.more, styles.centered]}
                >
                  {`已显示全部 ${search.total} 个场景`}
                </Typography.Text>
              ) : null}
            </>
          )}
        </View>
      </ScrollView>
    );
  },
);

function useStyles() {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        page: {
          width: '100%',
          maxWidth: rpx(768),
          alignSelf: 'center',
          paddingTop: rpx(16),
          paddingBottom: rpx(32),
        },
        padded: { paddingHorizontal: rpx(16) },
        chips: { marginTop: rpx(12) },
        section: { marginTop: rpx(20) },
        strip: { marginTop: rpx(12) },
        row: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        },
        actions: { flexDirection: 'row', alignItems: 'center', gap: rpx(12) },
        filterButton: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(6),
          borderRadius: rpx(10),
          borderWidth: 1,
          borderColor: aiColors.border,
          backgroundColor: aiColors.card,
          paddingHorizontal: rpx(12),
          paddingVertical: rpx(8),
        },
        filterOn: {
          borderColor: aiColors.primary,
          backgroundColor: tint(aiColors.primary, 0.08),
        },
        selected: { marginTop: rpx(12) },
        more: { marginTop: rpx(16), paddingVertical: rpx(8) },
        centered: { textAlign: 'center' },
        grid: { marginTop: rpx(12), gap: rpx(10) },
        gridWide: { flexDirection: 'row', flexWrap: 'wrap' },
        cellWide: { width: '49%' },
      }),
    [rpx],
  );
}
